/**
 * UserManagement Service
 * 사용자 관리 비즈니스 로직
 */

export class UserManagementService {
  /**
   * 날짜 포맷팅
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * 날짜 시간 포맷팅
   */
  formatDateTime(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  /**
   * 전화번호 포맷팅
   */
  formatPhoneNumber(phone: string): string {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
    }
    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  }

  /**
   * 선호 좌석 포맷팅
   */
  formatPreferredSeats(seats?: number[]): string {
    if (!seats || seats.length === 0) return '없음';
    if (seats.length <= 3) return seats.join(', ');
    return `${seats.slice(0, 3).join(', ')} 외 ${seats.length - 3}개`;
  }

  /**
   * 사용자 상태 라벨
   */
  getUserStatusLabel(isActive: boolean): string {
    return isActive ? '활성' : '비활성';
  }

  /**
   * 사용자 상태 색상
   */
  getUserStatusColor(isActive: boolean): string {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  }

  /**
   * 사용자 폼 검증
   */
  validateUserForm(
    name: string,
    phone: string,
    email?: string
  ): { isValid: boolean; error?: string } {
    if (!name.trim()) {
      return { isValid: false, error: '이름을 입력해주세요.' };
    }

    if (name.length > 50) {
      return { isValid: false, error: '이름은 50자 이내로 입력해주세요.' };
    }

    if (!phone.trim()) {
      return { isValid: false, error: '전화번호를 입력해주세요.' };
    }

    const phoneRegex = /^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/;
    if (!phoneRegex.test(phone.replace(/-/g, ''))) {
      return { isValid: false, error: '올바른 전화번호 형식이 아닙니다.' };
    }

    if (email && email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return { isValid: false, error: '올바른 이메일 형식이 아닙니다.' };
      }
    }

    return { isValid: true };
  }

  /**
   * 검색 필터링
   */
  filterUsers<T extends { name: string; phone: string; email?: string }>(
    users: T[],
    searchTerm: string
  ): T[] {
    if (!searchTerm.trim()) return users;

    const term = searchTerm.toLowerCase();
    return users.filter(
      (user) =>
        user.name.toLowerCase().includes(term) ||
        user.phone.includes(term) ||
        (user.email && user.email.toLowerCase().includes(term))
    );
  }
}

export const userManagementService = new UserManagementService();
